// src/hooks/useAuth.js
// AuthProvider + useAuth hook
// Provee user/staff de forma global usando React Context para evitar
// que cada componente refetche su propio estado de auth

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase, getCurrentUser, getCurrentStaff } from '../lib/supabase'
import { shouldSendWelcome } from '../lib/welcomeEmail'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [staff, setStaff] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Id del usuario ya cargado. Sirve para distinguir un LOGIN NUEVO (de deslogueado a
  // logueado) de un SIGNED_IN de la MISMA sesion (Supabase lo dispara al recuperar el
  // foco de la pestaña). En el segundo caso NO hay que re-armar loading: eso desmonta la
  // app y tira formularios/wizards en curso.
  const userIdRef = useRef(null)

  const loadStaffData = useCallback(async (currentUser) => {
    if (!currentUser) {
      setStaff(null)
      return
    }

    // Pasar el usuario ya conocido evita un segundo auth.getUser() (propenso a
    // deadlock si esto corre cerca del callback de onAuthStateChange).
    const { data: staffData, error: staffError } = await getCurrentStaff(currentUser)

    if (staffData) {
      setStaff(staffData)
      // Correo de bienvenida al primer login: fire-and-forget (SIN await, no
      // bloquea el render ni corre dentro del callback de onAuthStateChange).
      // El flag welcome_email_sent_at (reclamado atomicamente en la funcion)
      // evita reenvios en cargas posteriores.
      if (shouldSendWelcome(staffData)) {
        supabase.functions.invoke('send-welcome-email').catch(() => {})
      }
      return
    }

    // Re-fetch vacio (timeout/red/RLS transitorio): NO degradar a un usuario ya
    // cargado. El staff solo se limpia en signout real (rama sin sesion). Antes
    // este setStaff(null) hacia desaparecer el menu de admin al refrescar el token.
    // En la primera carga real sin registro, staff ya es null, asi que no se
    // concede admin por user_metadata (la fuente de verdad sigue siendo la tabla).
    if (staffError) {
      setError('Error al obtener datos del staff')
    }
  }, [])

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setLoading(false)
    }, 10000)

    const getInitialSession = async () => {
      try {
        const currentUser = await getCurrentUser()
        setUser(currentUser)
        userIdRef.current = currentUser?.id || null
        await loadStaffData(currentUser)
      } catch (err) {
        setError('Error de autenticacion')
      } finally {
        clearTimeout(loadingTimeout)
        setLoading(false)
      }
    }

    getInitialSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!session?.user) {
          setUser(null)
          setStaff(null)
          setError(null)
          setLoading(false)
          userIdRef.current = null
          return
        }

        const sameUser = userIdRef.current === session.user.id
        userIdRef.current = session.user.id

        // La misma persona conserva el MISMO objeto. Cada evento trae un objeto nuevo,
        // y todo lo que dependa de `user` (contextos, efectos) lo tomaba como un usuario
        // distinto y se recargaba: al volver del segundo plano en el telefono se
        // desmontaba la pantalla y se perdia el formulario. Solo USER_UPDATED trae
        // datos nuevos que valga la pena tomar.
        setUser((prev) => (prev && sameUser && event !== 'USER_UPDATED' ? prev : session.user))

        // TOKEN_REFRESHED / USER_UPDATED no cambian el registro de staff. Y un SIGNED_IN
        // de la MISMA sesion (Supabase lo dispara al recuperar el foco de la pestaña) no
        // es un login nuevo: re-armar loading desmontaria la app y tiraria un wizard o
        // formulario en curso (bug: al cambiar de pestaña se perdia el avance). En todos
        // esos casos no se recarga staff (evita degradar el rol) ni se toca loading.
        if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || sameUser) {
          setLoading(false)
          return
        }

        // Login NUEVO (de deslogueado a logueado): re-armar loading ANTES de la carga
        // diferida de staff, si no el gate de OrganizationContext puede parpadear
        // UnauthorizedOrg (flash de 1 frame) con user listo y loading=false.
        setLoading(true)

        // IMPORTANTE: no hacer await de llamadas a Supabase dentro del callback
        // de onAuthStateChange (puede deadlockear en supabase-js v2). Se difiere
        // fuera del callback con setTimeout(0).
        setTimeout(async () => {
          try {
            await loadStaffData(session.user)
            setError(null)
          } catch (err) {
            setError('Error de autenticacion')
          } finally {
            setLoading(false)
          }
        }, 0)
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [loadStaffData])

  const clearError = useCallback(() => setError(null), [])

  const refreshStaff = useCallback(async () => {
    const currentUser = await getCurrentUser()
    if (currentUser) {
      await loadStaffData(currentUser)
    }
  }, [loadStaffData])

  const value = useMemo(() => ({
    user,
    staff,
    coach: staff, // Backward compatibility
    loading,
    error,
    clearError,
    refreshStaff,
    isAuthenticated: !!user && !!staff,
    isPlatformAdmin: !!staff?.is_platform_admin,
  }), [user, staff, loading, error, clearError, refreshStaff])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
